import {
  useEnhancedProductStatus,
  useProductQuantityHighlight
} from '@components/UI-Components/ProductCard/ProductStatusHook'
import { PRODUCT_OPERATIONS_LOADERS } from '@modules/order/stores/view/List/ListDataStore'
import isNumber from 'lodash/isNumber'
import { OrderType } from '@modules/common/models/enums/OrderManagement'
import isEmpty from 'lodash/isEmpty'
import isEqual from 'lodash/isEqual'
import { useCallback, useMemo, useRef, useState } from 'react'
import { CURVE_TYPES } from '@modules/showroom/EnhancedOrder/constants/uiConstants'
import { getNumberFromLocale } from '@utils/get-number-form-locale'
import { getEnumValue } from '@utils/get-enum-value'
import { VIPClientType } from '@modules/common/models/enums/VIPClientType'
import { getKPICurrency } from '@modules/order/utils/get-kpi-currency'
import { IndexObjectStore } from '@stores/paginated-hierarchy-items/IndexObjectStore'
import { ProductCurvesStore } from '@stores/productCurvesStore'

export interface EnhancedProductQuantity {
  rowData: any
  itemIndexObjectStore: IndexObjectStore
  isOrderEditable: boolean
  isHorizontalProductDetailsView?: boolean
  orderType: string
  clientType: string
  clientId: string
  summary: any
  hideSplitQuantity: boolean
  getBookedDoors: Function
  copiedTemplateQuantities: any
  productCurveStore: ProductCurvesStore
  buyingSessionGroupId: string
  bulkUpdateDraftQuantity: Function
  updateProductQuantity: Function
  getSumOfDraftQuantityOfBookedDoors: Function
  getSizes: Function
  setIsQuantitiesUpdating?: Function
  setUpdatedQuantities?: Function
}

export const useEnhancedProductQuantity = ({
  rowData,
  itemIndexObjectStore,
  isOrderEditable,
  orderType,
  clientType,
  summary,
  hideSplitQuantity,
  getBookedDoors,
  copiedTemplateQuantities,
  productCurveStore,
  clientId,
  buyingSessionGroupId,
  bulkUpdateDraftQuantity,
  updateProductQuantity,
  getSumOfDraftQuantityOfBookedDoors,
  getSizes,
  isHorizontalProductDetailsView = false,
  setIsQuantitiesUpdating,
  setUpdatedQuantities
}: EnhancedProductQuantity) => {
  const rowDataId = rowData?.id
  const index = rowDataId ? itemIndexObjectStore.getIndexByKey(rowDataId) : null
  const [showSaveCopyTemplate, setShowSaveCopyTemplate] = useState(false)
  const [showCopiedTemplates, setShowCopiedTemplates] = useState(false)
  const [isGradingPopupOpen, setIsGradingPopupOpen] = useState(false)
  const [productCurves, setProductCurves] = useState(null)
  const [openDialog, setOpenDialog] = useState(() => ({
    open: false,
    curveTypeToBeUsed: CURVE_TYPES.DOOR_CURVE
  }))

  const isRetailView = clientType === getEnumValue(VIPClientType.Internal)
  const currency = getKPICurrency(orderType, summary)
  const inputRef = useRef<HTMLInputElement>(null)
  const hasSizes = !!getSizes(rowData?.id).length

  const isDoorOrder = orderType === OrderType.Door
  const bookedDoors =
    orderType === OrderType.Door
      ? summary?.door?.id
        ? [summary?.door?.id]
        : []
      : getBookedDoors(rowData?.id, summary?.doorOrders)
  const noBookedDoors = bookedDoors?.length === 0

  const isSplittingInProgress = isNumber(index)
    ? itemIndexObjectStore.isObjectDataLoading(
        index,
        PRODUCT_OPERATIONS_LOADERS.SPLITTING_CLIENT_ORDER_QUANTITY
      )
    : false

  const isRollupInProgress = itemIndexObjectStore.isObjectDataLoading(
    index,
    PRODUCT_OPERATIONS_LOADERS.UPDATING_CLIENT_ORDER_DRAFT_QUANTITY
  )

  const isAnyBookUnbookInProgress = isNumber(index)
    ? itemIndexObjectStore.isObjectDataLoading(
        index,
        PRODUCT_OPERATIONS_LOADERS.BOOK_UNBOOK_IN_PROGRESS
      )
    : false

  const isProductActive: boolean = useEnhancedProductStatus(rowData)

  const noGradeCurves = isEmpty(productCurves?.gradeCurve ?? {})
  const cmRating = productCurves?.cmRatingCurve?.curves?.weight ?? ''
  const gradeCurves = productCurves?.gradeCurve?.curves || {}
  const gradingAttributes = useMemo(() => {
    return rowData?.clientOrderProductGradingAttributes || {}
  }, [rowData])
  const bsgHasGradeCurves =
    !isRetailView && productCurveStore.hasGradeCurves(clientId, buyingSessionGroupId)
  const isGradingEnabledForProduct =
    !isRetailView &&
    !isEmpty(gradeCurves) &&
    !isDoorOrder &&
    isProductActive &&
    isOrderEditable

  const productQuantityValue = rowData?.clientOrderProductQuantity || 0
  const doorQuantityValue = rowData?.doorOrderProductDoorQuantity || 0

  const highlightInputBox = useProductQuantityHighlight(
    productQuantityValue,
    doorQuantityValue
  )
  const disableCopyTemplate =
    !isProductActive ||
    isSplittingInProgress ||
    isAnyBookUnbookInProgress ||
    !isOrderEditable ||
    noBookedDoors
  const disablePasteFromTemplate =
    !isProductActive ||
    isSplittingInProgress ||
    isAnyBookUnbookInProgress ||
    !isOrderEditable ||
    !copiedTemplateQuantities?.length
  const captureSize =
    summary?.client?.captureSize || summary?.clientOrder?.client?.captureSize
  const disableSplitingProductQty =
    !isProductActive ||
    isSplittingInProgress ||
    isAnyBookUnbookInProgress ||
    !isOrderEditable ||
    noBookedDoors ||
    productCurveStore.getCurveLoading(clientId, buyingSessionGroupId)
  const disableRollUp =
    !isProductActive ||
    isSplittingInProgress ||
    isRollupInProgress ||
    isAnyBookUnbookInProgress ||
    !isOrderEditable ||
    noBookedDoors
  const disableQuantityInput =
    !isOrderEditable ||
    isDoorOrder ||
    !isProductActive ||
    isSplittingInProgress ||
    isRollupInProgress ||
    isAnyBookUnbookInProgress ||
    (!noGradeCurves && !isRetailView) ||
    isSplittingInProgress

  const hideSplitDownIcon = useMemo(() => {
    return hideSplitQuantity && !isRetailView
  }, [hideSplitQuantity, isRetailView])

  const splitQuantityHandler = useCallback(
    (splitProductToDoors, splitDoorsToSizes) => {
      let curveTypeToBeUsed = splitDoorsToSizes
        ? CURVE_TYPES.SIZE_CURVE
        : CURVE_TYPES.DOOR_CURVE

      const selectedCurveData = productCurves?.[curveTypeToBeUsed] ?? {}
      if (isEmpty(selectedCurveData?.curves) && isEmpty(selectedCurveData?.doors)) {
        setOpenDialog(prevState => ({
          ...prevState,
          open: true,
          curveTypeToBeUsed: curveTypeToBeUsed
        }))
      } else {
        if (isHorizontalProductDetailsView) {
          setIsQuantitiesUpdating?.(true)
        }
        bulkUpdateDraftQuantity(
          summary,
          rowDataId,
          getNumberFromLocale(inputRef?.current?.value),
          splitProductToDoors,
          splitDoorsToSizes
        ).then(() => {
          if (isHorizontalProductDetailsView) {
            setUpdatedQuantities?.()
            setIsQuantitiesUpdating?.(false)
          }
        })
      }
    },
    [
      rowDataId,
      productCurves,
      bulkUpdateDraftQuantity,
      summary,
      isHorizontalProductDetailsView,
      setUpdatedQuantities,
      setIsQuantitiesUpdating
    ]
  )

  const rollUpProductQtyHandler = useCallback(
    (rollUpSizeToDoor, rollUpDoorToProduct) => {
      const defaultSplit = !rollUpSizeToDoor && !rollUpDoorToProduct
      if (isHorizontalProductDetailsView) {
        setIsQuantitiesUpdating?.(true)
      }
      updateProductQuantity(
        rowDataId,
        getSumOfDraftQuantityOfBookedDoors(rowDataId),
        null,
        true,
        defaultSplit || rollUpSizeToDoor,
        defaultSplit || rollUpDoorToProduct
      ).then(() => {
        if (isHorizontalProductDetailsView) {
          setUpdatedQuantities?.()
          setIsQuantitiesUpdating?.(false)
        }
      })
    },
    [
      rowDataId,
      isHorizontalProductDetailsView,
      updateProductQuantity,
      getSumOfDraftQuantityOfBookedDoors,
      setUpdatedQuantities,
      setIsQuantitiesUpdating
    ]
  )

  const handleDialogClose = () => {
    setOpenDialog(prevState => ({
      ...prevState,
      open: false
    }))
  }

  const handleGradingApply = useCallback(
    (productQty, selectedGrading) => {
      if (!isEqual(selectedGrading, gradingAttributes)) {
        updateProductQuantity(rowDataId, Number(productQty), selectedGrading).then(() => {
          if (isHorizontalProductDetailsView) {
            setUpdatedQuantities()
            setIsQuantitiesUpdating(false)
          }
        })
      }
      setIsGradingPopupOpen(false)
    },
    [
      rowDataId,
      gradingAttributes,
      isHorizontalProductDetailsView,
      setUpdatedQuantities,
      setIsQuantitiesUpdating,
      updateProductQuantity
    ]
  )

  const handleGradingCancel = () => {
    setIsGradingPopupOpen(false)
  }

  return {
    rowDataId,
    disableCopyTemplate,
    disablePasteFromTemplate,
    captureSize,
    disableQuantityInput,
    disableSplitingProductQty,
    disableRollUp,
    noBookedDoors,
    isDoorOrder,
    isGradingEnabledForProduct,
    cmRating,
    gradeCurves,
    gradingAttributes,
    bsgHasGradeCurves,
    highlightInputBox,
    productQuantityValue,
    doorQuantityValue,
    productCurves,
    showSaveCopyTemplate,
    setShowSaveCopyTemplate,
    showCopiedTemplates,
    setShowCopiedTemplates,
    isGradingPopupOpen,
    setIsGradingPopupOpen,
    setProductCurves,
    openDialog,
    setOpenDialog,
    handleGradingCancel,
    handleGradingApply,
    handleDialogClose,
    splitQuantityHandler,
    rollUpProductQtyHandler,
    currency,
    inputRef,
    hasSizes,
    hideSplitDownIcon
  }
}
